import { useState } from "react";
import { sharePlaatplan } from "../content/supportActions";
import { isMobileAppPromoEnabled } from "../config/featureFlags";
import { getMobileAppStoreLinks } from "../config/mobileAppLinks";
import { useLocale } from "../i18n/useLocale";
import { IconButton } from "./IconButton";
import { Modal } from "./Modal";

export function SupportModal({ supportLinks, onFeedback, onClose }) {
  const { display } = useLocale();
  const [shareStatus, setShareStatus] = useState("");
  const showAppSection = isMobileAppPromoEnabled();
  const appStoreLinks = getMobileAppStoreLinks();
  const hasAppStoreLinks = Boolean(appStoreLinks.appStore || appStoreLinks.playStore);

  const shareStatusMessages = {
    shared: display.t("web.supportModal.shareStatusShared"),
    copied: display.t("web.supportModal.shareStatusCopied"),
    unsupported: display.t("web.supportModal.shareStatusUnsupported"),
  };

  const handleShare = async () => {
    setShareStatus("");
    try {
      const result = await sharePlaatplan({ shareText: display.t("web.seo.shareText") });
      if (result.status === "cancelled") return;
      setShareStatus(shareStatusMessages[result.status] || "");
    } catch {
      setShareStatus(shareStatusMessages.unsupported);
    }
  };

  const handleFeedback = () => {
    onFeedback?.();
    onClose();
  };

  return (
    <Modal
      titleId="support-modal-title"
      onClose={onClose}
      className="info-modal support-modal"
      backdropClassName="modal-backdrop info-modal-backdrop"
      header={(
        <div className="info-modal-header">
          <div>
            <p className="info-page-kicker">{display.t("web.supportModal.kicker")}</p>
            <h2 id="support-modal-title">{display.t("web.supportModal.title")}</h2>
          </div>
          <IconButton label={display.t("web.shell.closeNotice")} onClick={onClose}>×</IconButton>
        </div>
      )}
    >
      <div className="info-modal-body support-modal-body">
        <p className="support-modal-text">{display.t("web.supportModal.intro1")}</p>
        <p className="support-modal-text">{display.t("web.supportModal.intro2")}</p>

        <section className="support-modal-section support-modal-section-muted" aria-labelledby="support-web-heading">
          <h3 id="support-web-heading" className="support-modal-section-title">
            {display.t("web.supportModal.webHeading")}
          </h3>
          <p className="support-modal-section-note">{display.t("web.supportModal.webNote")}</p>
        </section>

        {supportLinks.length > 0 ? (
          <section className="support-modal-section" aria-labelledby="support-monetary-heading">
            <h3 id="support-monetary-heading" className="support-modal-section-title">
              {display.t("web.supportModal.monetaryHeading")}
            </h3>
            <p className="support-modal-section-note">{display.t("web.supportModal.monetaryNote")}</p>
            <ul className="support-option-list">
              {supportLinks.map((link) => (
                <li key={link.id}>
                  <a
                    href={link.url}
                    className="support-option-link support-option-link-external"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <span className="support-option-link-label">{link.label}</span>
                    <span className="support-option-link-hint">{link.hint}</span>
                  </a>
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        {showAppSection ? (
          <section className="support-modal-section support-modal-section-app" aria-labelledby="support-app-heading">
            <h3 id="support-app-heading" className="support-modal-section-title">
              {display.t("web.supportModal.appHeading")}
            </h3>
            <p className="support-modal-section-note">{display.t("web.supportModal.appNote")}</p>
            {hasAppStoreLinks ? (
              <ul className="support-option-list">
                {appStoreLinks.appStore ? (
                  <li>
                    <a
                      href={appStoreLinks.appStore}
                      className="support-option-link support-option-link-external"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <span className="support-option-link-label">
                        {display.t("web.supportModal.appStoreLabel")}
                      </span>
                      <span className="support-option-link-hint">
                        {display.t("web.supportModal.appStoreHint")}
                      </span>
                    </a>
                  </li>
                ) : null}
                {appStoreLinks.playStore ? (
                  <li>
                    <a
                      href={appStoreLinks.playStore}
                      className="support-option-link support-option-link-external"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <span className="support-option-link-label">
                        {display.t("web.supportModal.playStoreLabel")}
                      </span>
                      <span className="support-option-link-hint">
                        {display.t("web.supportModal.playStoreHint")}
                      </span>
                    </a>
                  </li>
                ) : null}
              </ul>
            ) : (
              <p className="support-modal-section-note">{display.t("web.supportModal.appComingSoon")}</p>
            )}
          </section>
        ) : null}

        <section className="support-modal-section" aria-labelledby="support-community-heading">
          <h3 id="support-community-heading" className="support-modal-section-title">
            {display.t("web.supportModal.communityHeading")}
          </h3>
          <ul className="support-option-list">
            <li>
              <button
                type="button"
                className="support-option-link"
                onClick={handleShare}
              >
                <span className="support-option-link-label">{display.t("web.supportModal.shareLabel")}</span>
                <span className="support-option-link-hint">{display.t("web.supportModal.shareHint")}</span>
              </button>
            </li>
            <li>
              <button
                type="button"
                className="support-option-link"
                onClick={handleFeedback}
              >
                <span className="support-option-link-label">{display.t("web.supportModal.feedbackLabel")}</span>
                <span className="support-option-link-hint">{display.t("web.supportModal.feedbackHint")}</span>
              </button>
            </li>
          </ul>
          {shareStatus ? (
            <p className="support-modal-status" role="status">{shareStatus}</p>
          ) : null}
        </section>

        <div className="support-modal-actions">
          <button type="button" className="secondary-button" onClick={onClose}>
            {display.t("web.supportModal.dismiss")}
          </button>
        </div>
      </div>
    </Modal>
  );
}
